import {ApiError, Fetcher} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {DesignSnapshotResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignSnapshotResponse';
import {DesignSnapshotsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignSnapshotsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    GuideJson,
    GuideProps,
    GuideState,
    GuideStep,
    GuideTour,
    GuideVideo
} from '@mintdata-wsp/common/state/ui/visualDesigner/GuideState';
import {CookieKey, CookieUtil, CookieValue} from '@mintdata-wsp/common/util/CookieUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action, runInAction, toJS} from 'mobx';

export class GuideController
{
    private static GUIDE_FIRST_OPENING_TIME_COOKIE_KEY = 'guideFirstOpeningTime';
    private static GUIDE_LAST_OPENING_TIME_COOKIE_KEY = 'guideLastOpeningTime';

    private fetcher:Fetcher;

    private jsonLoadingPromise:Promise<any>;

    public constructor()
    {
        this.fetcher = new Fetcher();
        this.jsonLoadingPromise = null;
    }

    public init():void
    {
    }

    public getGuideState():GuideState | null
    {
        const {guideState} = rootState.ui.visualDesigner;

        return guideState ? guideState : null;
    }

    @action
    public resetGuideState()
    {
        const {guideState} = rootState.ui.visualDesigner;

        guideState.props = null;
        guideState.json = null;
        guideState.isDesignLoading = false;
        guideState.tour = new GuideTour();
        guideState.video = new GuideVideo();
    }

    @action
    public setDesignLoadingTo(isLoading:boolean)
    {
        const {guideState} = rootState.ui.visualDesigner;

        guideState.isDesignLoading = isLoading;
    }

    @action
    public async workWithLoadedGuideDesign(guideProps:GuideProps)
    {
        // console.log('%c workWithLoadedGuideDesign: guideProps =', 'background:#0f0;color:#000;', guideProps);

        const {guideState} = rootState.ui.visualDesigner;

        guideState.props = guideProps;

        const {guideUrl} = guideProps;

        this.setDesignLoadingTo(true);

        await this._loadGuideJson(guideUrl);

        const {props, json} = guideState;
        // console.log('%c loadGuideJson.then: props, guide =', 'background:#0f0;color:#000;', toJS(props), toJS(json));

        const firstOpeningTime:CookieValue = this._getGuideCookieFirstOpeningTime();

        const isOpenedOnce:boolean = !!firstOpeningTime;

        // console.log('%c _loadGuideJson: isOpenedOnce =', 'background:#080;color:#000;', isOpenedOnce);

        if( isOpenedOnce ) // Guide was opened at least once
        {

            // if( props.lastOpenedGuidePage ) // someone opened the design !!!
            // {
            //     // // TODO remove after tests
            //     // if( props.lastOpenedGuidePage )
            //     // {
            //     //     runInAction(() =>
            //     //     {
            //     //         guideState.tour.isVisible = true;
            //     //         controllers.ui.lx.setDotButtonZIndex(1);
            //     //     });
            //     // }
            //     //
            //     // this._preloadDesignsForStep(props.lastOpenedGuidePage);
            //     // this.setDesignLoadingTo(false);
            // }
            // else
            // {
            //     // runInAction(() =>
            //     // {
            //     //     guideState.tour.isVisible = true;
            //     //     controllers.ui.lx.setDotButtonZIndex(1);
            //     // });
            //     // this.loadDesignForStep(1);
            // }

            if( props.lastOpenedGuidePage ) // Guide was opened at least once
            {
                this._preloadDesignsForStep(props.lastOpenedGuidePage);
            }
            else
            {
                // ??? it's impossible
                runInAction(() =>
                {
                    guideState.props.lastOpenedGuidePage = 1;
                    this.loadDesignForStep(1);
                });
            }

            this.setDesignLoadingTo(false);
        }
        else // open the Guide for the first time
        {
            this.setTourVisibility(true);
            this.loadDesignForStep(1);
            this._applyGuideUIState(json);
        }

        this._setGuideCookie();
    }

    private _loadGuideJson(guideUrl:string):Promise<any>
    {
        return this._guideJsonFetch(guideUrl)
            .then(action((guideJson:GuideJson) =>
            {
                if( guideJson instanceof ApiError )
                {
                    this._errorToast();
                    return;
                }

                const {guideState} = rootState.ui.visualDesigner;
                // console.log('%c loadGuideJson: guide =', 'background:#00f;color:#ff0;', guideJson);

                guideState.json = guideJson;
            }));
    }

    private async _guideJsonFetch(guideJsonUrl:string):Promise<GuideJson>
    {
        // const cdnUrl = 'https://cdn.mintdata.com';

        // guideJsonUrl = `${cdnUrl}/guides/getting_started.json`;
        // guideJsonUrl = `${cdnUrl}/guides/getting_started_tmp.json`;

        if( !this.jsonLoadingPromise )
            this.jsonLoadingPromise = this.fetcher.request('get', guideJsonUrl, null);

        return this.jsonLoadingPromise;
    }

    @action.bound
    public async loadDesignForStep(step:number)
    {
        // console.log('%c loadDesignForStep: step =', 'background:#0f0;color:#000;', step);

        const guideState:GuideState = this.getGuideState();
        const {json: guideJson} = guideState;

        const guideStep:GuideStep = guideJson.steps[step - 1];
        if( !guideStep )
        {
            this._errorToast();
            return;
        }

        let designJson = guideStep.design;

        if( !designJson )
        {
            designJson = await this._preloadDesignsForStep(step, true);

            if( !designJson )
            {
                this._errorToast();
                return;
            }
        }
        else
        {
            this._preloadDesignsForStep(step);
        }

        designJson = JSON.parse(designJson);

        runInAction(() =>
        {
            guideState.props.lastOpenedGuidePage = step;

            const {designSync, openDesign} = controllers.ui;

            designSync.syncGuideDesign(designJson);

            if( openDesign.getDesignState().surface )
                openDesign.setActivePageById(openDesign.getDesignState().surface.startPageId);
        });
    }

    private async _preloadDesignsForStep(step:number, waitForLoad:boolean = false)
    {
        // console.log('%c preloadDesignsForStep: step =', 'background:#0f0;color:#000;', step);

        const guideState:GuideState = this.getGuideState();
        const {json: guideJson} = guideState;

        if( !guideJson ) return;

        const designId:number = guideJson.designId;

        const guideStep:GuideStep = guideJson.steps[step - 1];
        if( !guideStep )
        {
            this._errorToast();
            return;
        }

        const versionNames:string = this._getNeededPrevNextVersionNames(guideJson, step);
        // console.log('%c preloadDesignsForStep: versionNames =', 'background:#080;color:#000;', versionNames);

        if( !versionNames ) return;

        if( waitForLoad )
        {
            const response:DesignSnapshotsResponse = await API.sample.designVersionsList(designId, versionNames);
            // console.log('%c preloadDesignsForStep: [waitForLoad] response =', 'background:#080;color:#000;', response);

            return this._processDesignVersions(response, guideJson, guideStep.versionName);
        }

        setTimeout(() =>
        {
            API.sample
                .designVersionsList(designId, versionNames)
                .then((response:DesignSnapshotsResponse) =>
                {
                    // console.log('%c preloadDesignsForStep: [setTimeout] response =', 'background:#ff0;color:#000;', response);
                    this._processDesignVersions(response, guideJson, guideStep.versionName);
                });
        });
    }

    private _getNeededPrevNextVersionNames(guideJson:GuideJson, step:number):string
    {
        const prevStep:GuideStep = step >= 2 && guideJson.steps[step - 2];
        const currentStep:GuideStep = guideJson.steps[step - 1];
        const nextStep:GuideStep = step < guideJson.steps.length && guideJson.steps[step];

        let versionNames:string[] = [];

        if( prevStep && !prevStep.design ) versionNames.push(prevStep.versionName);
        if( currentStep && !currentStep.design ) versionNames.push(currentStep.versionName);
        if( nextStep && !nextStep.design ) versionNames.push(nextStep.versionName);

        return versionNames.join(',');
    }

    @action.bound
    private _processDesignVersions(response:DesignSnapshotsResponse, guideJson:GuideJson, currentVersionName:string):any
    {
        let currentDesign:any = null;

        response.designSnapshots.forEach((designSnapshot:DesignSnapshotResponse) =>
        {
            const design = designSnapshot.designerState;
            const versionName = designSnapshot.name;

            if( versionName === currentVersionName )
            {
                currentDesign = design;
            }

            this._setDesignForVersion(guideJson, design, versionName);
        });

        return currentDesign;
    }

    private _setDesignForVersion(guideJson:GuideJson, design:string, versionName:string)
    {
        guideJson.steps.forEach((step:GuideStep) =>
        {
            if( step.versionName === versionName )
            {
                step.design = design;
            }
        });
    }

    @action
    public setTourVisibility(isVisible:boolean)
    {
        const guideState:GuideState = this.getGuideState();

        guideState.tour.isVisible = isVisible;
        controllers.ui.lx.setDotButtonZIndex(isVisible ? 1 : null);
    }

    @action
    public setVideoVisibility(isVisible:boolean)
    {
        const guideState:GuideState = this.getGuideState();

        guideState.video.isVisible = isVisible;
        controllers.ui.lx.setDotButtonZIndex(isVisible ? 1 : null);
    }

    private _errorToast(message?:string)
    {
        controllers.ui.toasts.showError({message: message || 'GUIDE: Oops!'});
    }

    private _getGuideCookieFirstOpeningTime():CookieValue
    {
        return CookieUtil.getCookie(GuideController.GUIDE_FIRST_OPENING_TIME_COOKIE_KEY);
    }

    private _getGuideCookieLastOpeningTime():CookieValue
    {
        return CookieUtil.getCookie(GuideController.GUIDE_LAST_OPENING_TIME_COOKIE_KEY);
    }

    private _setGuideCookie()
    {
        const first = this._getGuideCookieFirstOpeningTime();

        const rootDomain:string = WindowLocationUtil.getHostname();
        const cookieValue:CookieValue = (Date.now()).toString();

        if( !first )
        {
            const cookieKey:CookieKey = GuideController.GUIDE_FIRST_OPENING_TIME_COOKIE_KEY;

            CookieUtil.setTempCookie(rootDomain, cookieKey, cookieValue);
        }

        const cookieKey:CookieKey = GuideController.GUIDE_LAST_OPENING_TIME_COOKIE_KEY;

        CookieUtil.setTempCookie(rootDomain, cookieKey, cookieValue);
    }

    private _applyGuideUIState(json:GuideJson):void
    {
        const {designUIState} = controllers.ui;

        if( !json.initialUIState )
            return;

        designUIState.applyState(json.initialUIState, true);
    }
}
